import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Brain, Loader2, Trophy } from "lucide-react";
import { Reveal, Eyebrow } from "./Reveal";
import api from "../lib/api";

const QuizSection = () => {
  const [quiz, setQuiz] = useState(undefined); // undefined = loading, null = none live
  const [top, setTop] = useState(null);

  useEffect(() => {
    api.get("/quiz/active").then((r) => setQuiz(r.data)).catch(() => setQuiz(null));
  }, []);

  useEffect(() => {
    if (quiz && quiz.id) {
      api.get(`/quiz/${quiz.id}/leaderboard`)
        .then((r) => setTop((r.data || [])[0] || null))
        .catch(() => {});
    }
  }, [quiz]);

  return (
    <section id="quiz" className="relative py-16 md:py-24 scroll-mt-20" data-testid="quiz-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
        <Reveal>
          <Eyebrow>Daily quiz</Eyebrow>
        </Reveal>
        {quiz === undefined ? (
          <div className="mt-8 grid place-items-center py-12 text-muted-foreground">
            <Loader2 className="h-6 w-6 animate-spin" />
          </div>
        ) : quiz === null ? (
          <Reveal delay={0.05}>
            <div className="mt-6 rounded-3xl border-2 border-dashed border-foreground/15 p-10 text-center" data-testid="quiz-section-empty">
              <Brain className="h-8 w-8 text-pitch dark:text-turf mx-auto" />
              <p className="mt-3 font-display font-bold text-lg">No quiz today — the next one drops soon</p>
              <p className="text-sm text-muted-foreground mt-1">Ten questions, every age group, and a leaderboard to top.</p>
            </div>
          </Reveal>
        ) : (
          <Reveal delay={0.05}>
            <div className="mt-6 rounded-3xl bg-pitch text-cream dark:bg-card p-6 md:p-10 relative overflow-hidden" data-testid="quiz-section-card">
              <div className="absolute -top-10 -right-10 opacity-10" aria-hidden="true">
                <Brain className="h-44 w-44" />
              </div>
              <div className="grid md:grid-cols-12 gap-8 items-center relative">
                <div className="md:col-span-7">
                  <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-turf">Today's 10 questions</p>
                  <h3 className="mt-2 font-display text-2xl sm:text-3xl font-bold tracking-tight">{quiz.title}</h3>
                  <p className="mt-2 text-sm text-cream/80 dark:text-muted-foreground">
                    {quiz.question_count} questions · every age group · right or wrong, no points —
                    the leaderboard remembers who was sharpest and fastest.
                  </p>
                  {top && (
                    <p className="mt-3 inline-flex items-center gap-2 rounded-full bg-cream/10 px-4 py-1.5 text-xs font-semibold" data-testid="quiz-top-scorer">
                      <Trophy className="h-3.5 w-3.5 text-turf" /> Leader so far: {top.name} — {top.correct}/{quiz.question_count}
                    </p>
                  )}
                  <div className="mt-6 flex flex-wrap gap-3">
                    <Link
                      to="/quiz"
                      data-testid="quiz-play-cta"
                      className="inline-flex items-center gap-2 h-12 px-7 rounded-full bg-cream text-pitch font-semibold hover:bg-white transition-colors dark:bg-turf dark:text-background"
                    >
                      <Brain className="h-4 w-4" /> Play today's quiz
                    </Link>
                    <Link to="/quiz" className="inline-flex items-center gap-1.5 h-12 px-6 rounded-full border-2 border-cream/30 dark:border-foreground/15 font-semibold text-sm hover:bg-cream/10 dark:hover:bg-muted transition-colors">
                      Leaderboard <ArrowUpRight className="h-4 w-4" />
                    </Link>
                  </div>
                </div>
                <div className="md:col-span-5" data-testid="quiz-leaderboard-mini">
                  <MiniBoard quizId={quiz.id} />
                </div>
              </div>
            </div>
          </Reveal>
        )}
      </div>
    </section>
  );
};

const MiniBoard = ({ quizId }) => {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    api.get(`/quiz/${quizId}/leaderboard`).then((r) => setRows(r.data || [])).catch(() => setRows([]));
  }, [quizId]);

  return (
    <div className="rounded-3xl bg-cream/10 dark:bg-background/40 p-5">
      <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-turf">Leaderboard</p>
      {rows === null ? (
        <p className="mt-4 text-xs text-cream/70">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="mt-4 text-sm text-cream/80">No one has played yet — be the first name on the board!</p>
      ) : (
        <ol className="mt-4 space-y-2">
          {rows.slice(0, 3).map((r, i) => (
            <li key={i} className="flex items-center gap-3 text-sm">
              <span className={`grid place-items-center h-7 w-7 rounded-full font-mono text-[11px] shrink-0 ${i === 0 ? "bg-flame text-white font-bold" : "bg-cream/15"}`}>
                {String(i + 1).padStart(2, "0")}
              </span>
              <span className="font-semibold flex-1 truncate">{r.name}</span>
              <span className="font-mono text-xs text-turf">{r.correct}/10</span>
              <span className="font-mono text-[11px] text-cream/60">
                {Math.round(r.time_ms / 1000) < 60 ? `${Math.round(r.time_ms / 1000)}s` : `${Math.floor(Math.round(r.time_ms / 1000) / 60)}m ${Math.round(r.time_ms / 1000) % 60}s`}
              </span>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
};

export default QuizSection;
